import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Category {
  _id: string;
  name: string;
  nameArabic?: string;
  slug: string;
  type: 'main' | 'sub';
  active: boolean;
  order: number;
  description?: string;
  descriptionArabic?: string;
  icon?: string;
}

export default function CategoriesTable() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const isRtl = i18n.language === 'ar';
  const [loading, setLoading] = useState(true);
  const [_showFilters, _setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The search term actually used for API calls
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [activeSearch, activeFilter]);

  useEffect(() => {
    loadCategories();
  }, [currentPage, limit, activeSearch, activeFilter]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params: any = { 
        type: 'main', 
        page: currentPage,
        limit: limit,
      };
      
      if (activeSearch) params.search = activeSearch;
      if (activeFilter === 'active') params.activeOnly = 'true';
      if (activeFilter === 'inactive') {
        params.activeOnly = 'false';
      }
      
      const response = await api.get('/admin/categories', { params });
      setCategories(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('categories.confirmDelete'))) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      loadCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.message || t('categories.failedToDelete'));
    }
  };


  if (loading) {
    return <div className="text-center py-8">{t('categories.loading')}</div>;
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('categories.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
            />
            <button
              onClick={handleSearch}
              className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('categories.allStatus')}</option>
              <option value="active">{t('categories.active')}</option>
              <option value="inactive">{t('categories.inactive')}</option>
            </select>
            <button
              onClick={() => navigate('/categories/new')}
              className="bg-primary-orange hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              + {t('categories.addCategory')}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.name')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.arabicName')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.slug')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.order')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.status')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {category.icon && <span className="mr-2">{category.icon}</span>}
                      <div className="text-sm font-medium text-gray-900">{category.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.nameArabic || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        category.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {category.active ? t('categories.active') : t('categories.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                    <button
                      onClick={() => navigate(`/categories/${category._id}/edit`)}
                      className="text-primary-orange hover:text-orange-600"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(category._id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      {t('common.delete')}
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {categories.length === 0 && (
          <div className="text-center py-8 text-gray-500">{t('categories.noCategories')}</div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">
                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} categories
              </span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary-orange text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
